import { useState } from "react";
import { ArrowUp, CircleNotch, PencilSimple } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FlagMarker } from "@/components/observer/FlagMarker";
import { FocusField } from "@/components/observer/FocusField";
import { fmtClock } from "@/lib/format";

// One tap each, mid-meeting. Phrased for any kind of conversation; the last sends an empty question, which is the
// Observer's open read in light of its focus.
const CHIPS = [
  ["What are we missing?", "What are we missing?"],
  ["Where's the tension?", "Where is the real tension here, and what does each side need?"],
  ["What's still undecided?", "What is still undecided, or agreed without an owner or a date?"],
  ["What should we ask next?", "What is the one question we should answer next?"],
  ["Your read so far", ""],
];

// What the Observer is helping with. One line; editable during the meeting, applies to the next answer.
const Focus = ({ meta, onFocus }) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const editable = meta.status === "active" && !!onFocus;
  const save = async () => {
    setSaving(true);
    try {
      await onFocus(draft);
      setDraft(null);
    } finally {
      setSaving(false);
    }
  };
  if (draft !== null) {
    return (
      <div className="space-y-2" data-testid="focus-editor">
        <FocusField value={draft} onChange={setDraft} rows={3} autoFocus testid="session-focus" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setDraft(null)} className="h-8 px-3 text-xs border border-[var(--o-border)] hover:bg-[var(--o-surface)]" data-testid="focus-cancel">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="h-8 px-3 text-xs font-semibold bg-[var(--o-fg)] text-[var(--o-bg)] hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5" data-testid="focus-save">
            {saving && <CircleNotch size={12} className="animate-spin" />} Use this focus
          </button>
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => editable && setDraft(meta.purpose || "")}
      disabled={!editable}
      className="group w-full text-left flex items-start gap-2 text-xs text-[var(--o-muted)] hover:text-[var(--o-fg)] disabled:hover:text-[var(--o-muted)] transition-colors"
      title={editable ? "Change what the Observer helps with" : undefined}
      data-testid="focus-line"
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--o-muted3)] pt-0.5 shrink-0">Focus</span>
      <span className="line-clamp-2 flex-1" data-testid="focus-text">{meta.purpose || "None set. Tap to tell the Observer what to help with."}</span>
      {editable && <PencilSimple size={12} className="shrink-0 mt-0.5 opacity-50 group-hover:opacity-100" />}
    </button>
  );
};

export const AskBox = ({ meta, onAsk, onPullFlag, asking, onFocus }) => {
  const [q, setQ] = useState("");
  // Gate on audio that actually transcribed; failed chunks advance the clock but carry no words.
  const heard = meta.successful_transcript_seconds ?? meta.transcript_seconds;
  const remaining = meta.min_transcript_seconds - heard;
  const noSpeech = meta.has_speech === false;
  const locked = remaining > 0 || noSpeech;
  const disabled = locked || asking || meta.status !== "active";

  const submit = () => {
    if (disabled) return;
    onAsk(q);
    setQ("");
  };

  const button = (
    <button
      onClick={submit}
      disabled={disabled}
      className="h-10 px-5 flex items-center gap-2 bg-[var(--o-fg)] text-[var(--o-bg)] text-sm font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
      data-testid="ask-button"
    >
      {asking ? <CircleNotch size={15} className="animate-spin" /> : <ArrowUp size={15} weight="bold" />} Ask
    </button>
  );

  return (
    <div className="p-5 sm:p-6 border-b border-[var(--o-border)] space-y-3 bg-[var(--o-bg2)]" data-testid="ask-box">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--o-muted2)]">Ask the Observer</span>
        <span className="font-mono text-[11px] text-[var(--o-muted3)]">empty = open read</span>
      </div>
      <Focus meta={meta} onFocus={onFocus} />
      <textarea
        autoComplete="off"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        rows={2}
        placeholder="Ask anything about the conversation. Leave empty for its read so far."
        className="w-full resize-none bg-[var(--o-bg)] border border-[var(--o-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--o-fg)] transition-colors placeholder:text-[var(--o-muted3)]"
        data-testid="ask-input"
      />
      <div className="flex flex-wrap gap-1.5" data-testid="ask-chips">
        {CHIPS.map(([label, question]) => (
          <button
            key={label}
            type="button"
            onClick={() => !disabled && onAsk(question)}
            disabled={disabled}
            className="px-2.5 py-1 text-xs border border-[var(--o-border)] text-[var(--o-muted)] hover:text-[var(--o-fg)] hover:border-[var(--o-border-strong)] hover:bg-[var(--o-surface)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="ask-chip"
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <FlagMarker flag={meta.current_flag} onPull={onPullFlag} busy={asking} />
        {meta.flag_check_retrying && !meta.current_flag && (
          <span className="font-mono text-[11px] text-[var(--o-muted3)]" title="The last background flag check failed; the same stretch of conversation will be checked again." data-testid="flag-check-retrying">
            flag check retrying…
          </span>
        )}
        <div className="ml-auto">
          {locked ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} data-testid="ask-button-locked">{button}</span>
              </TooltipTrigger>
              <TooltipContent className="bg-[var(--o-fg)] text-[var(--o-bg)] rounded-none font-mono text-xs" data-testid="ask-locked-tooltip">
                {remaining > 0
                  ? `The Observer needs ${fmtClock(meta.min_transcript_seconds)} of transcribed speech · ${fmtClock(remaining)} to go`
                  : "No speech has been transcribed yet"}
              </TooltipContent>
            </Tooltip>
          ) : (
            button
          )}
        </div>
      </div>
    </div>
  );
};
